import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { get } from "helpers/api_helper"

const keyOf = (item, start) => `${item.key}|${start}`
// A tournament the warnings concern: a proposal, and a published event, whose neighbours would carry them (Phase E3) unless it is cancelled
// or deactivated: then it takes part in none (ticket 13).
export const takesPartInWarnings = item => item?.kind === "proposal" || (item?.kind === "event" && !item.cancelled)

// The warnings a proposal would have at other start days (Phase C1w, ticket 13 place 6), which only the server's engine computes: the
// Season drag's preview, a move's toast and "Move a confirmed proposal?"; for a published event, those its neighbours would have with it
// there (its move dialog, Phase E3). `load(item, starts)` asks once for the days not asked yet (a drag asks for every week of the season at
// once); `at(item, start)` is { status: loading | ready | failed, warnings } or null. The answers hold for the calendar read they were asked
// with: with a new read, `load` and `at` change, so what shows asks again. A read that fails never stops a move.
export default function useMoveWarnings(read) {
  const answers = useRef({ read, byKey: new Map() })
  const [, setVersion] = useState(0)
  const mounted = useRef(true)
  useEffect(() => () => { mounted.current = false }, [])
  const answersOfRead = useCallback(() => {
    if (answers.current.read !== read) answers.current = { read, byKey: new Map() }
    return answers.current.byKey
  }, [read])
  const load = useCallback((item, starts) => {
    if (!takesPartInWarnings(item)) return
    const byKey = answersOfRead()
    const asked = [...new Set(starts)].filter(start => start !== item.days.start && !byKey.has(keyOf(item, start)))
    if (!asked.length) return
    const settle = (start, answer) => { if (byKey.get(keyOf(item, start))?.status === "loading") byKey.set(keyOf(item, start), answer) }
    asked.forEach(start => byKey.set(keyOf(item, start), { status: "loading" }))
    setVersion(version => version + 1)
    get(`/admin/competitive-calendar/move-warnings?kind=${item.kind}&id=${item.id}&starts=${asked.join(",")}`)
      .then(response => (response?.data?.placements || []).forEach(({ days, warnings }) => settle(days.start, { status: "ready", warnings })))
      .catch(() => {})
      .then(() => {
        asked.forEach(start => settle(start, { status: "failed" }))
        if (mounted.current) setVersion(version => version + 1)
      })
  }, [answersOfRead])
  const at = useCallback((item, start) => (item ? answersOfRead().get(keyOf(item, start)) || null : null), [answersOfRead])
  return useMemo(() => ({ load, at }), [load, at])
}
